import { mdiCogOutline } from "@mdi/js";
import { allLocales, useIntlDisplayNames } from "@typeblind/intl";
import {
  type AnyUser,
  Avatar,
  type PageInfo,
  Pages,
  usePageData,
} from "@typeblind/pages-shared";
import { useTheme } from "@typeblind/themes";
import { Icon, IconButton, Popover } from "@typeblind/widget";
import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { NavLink } from "react-router";
import { Logo } from "./Logo.tsx";
import * as styles from "./NavMenu.module.less";

// Approximate flag for each supported locale. Language and country are not
// the same thing (e.g. Esperanto has no country), so this is a best-effort
// visual aid, not a claim about where a language "belongs".
const localeFlags: Record<string, string> = {
  "en": "🇬🇧",
  "af": "🇿🇦",
  "ar": "🇸🇦",
  "bg": "🇧🇬",
  "ca": "🇪🇸",
  "cs": "🇨🇿",
  "da": "🇩🇰",
  "de": "🇩🇪",
  "el": "🇬🇷",
  "eo": "🌐",
  "es": "🇪🇸",
  "et": "🇪🇪",
  "fa": "🇮🇷",
  "fi": "🇫🇮",
  "fr": "🇫🇷",
  "ga": "🇮🇪",
  "he": "🇮🇱",
  "hr": "🇭🇷",
  "hu": "🇭🇺",
  "id": "🇮🇩",
  "it": "🇮🇹",
  "ja": "🇯🇵",
  "ko": "🇰🇷",
  "ne": "🇳🇵",
  "nl": "🇳🇱",
  "pl": "🇵🇱",
  "pt-br": "🇧🇷",
  "pt-pt": "🇵🇹",
  "ro": "🇷🇴",
  "ru": "🇷🇺",
  "sk": "🇸🇰",
  "sv": "🇸🇪",
  "th": "🇹🇭",
  "tr": "🇹🇷",
  "uk": "🇺🇦",
  "vi": "🇻🇳",
  "zh-hans": "🇨🇳",
  "zh-hant": "🇹🇼",
  "zh-tw": "🇹🇼",
};

function localeFlag(locale: string): string {
  return localeFlags[locale] ?? "🌐";
}

export function NavMenu({ currentPath }: { readonly currentPath: string }) {
  const { publicUser, user } = usePageData();
  // Mirror the signed-in display name into a readable, same-origin cookie so the
  // separate blog app (served under /blog) can render the same avatar/CTA as the
  // app's nav. Cleared when signed out. (The session cookie is httpOnly, so the
  // blog can't read the name from it directly.)
  useEffect(() => {
    const name = publicUser.id != null ? publicUser.name : null;
    document.cookie = name
      ? `tb_user=${encodeURIComponent(name)};path=/;max-age=1209600;samesite=lax`
      : "tb_user=;path=/;max-age=0;samesite=lax";
  }, [publicUser.id, publicUser.name]);
  return (
    <div className={styles.root}>
      <div className={styles.logoWrap}>
        <NavLink
          to={Pages.landing.path}
          className={styles.logoLink}
          aria-label="TypeBlind home"
        >
          <Logo />
        </NavLink>
      </div>

      <div className={styles.links}>
        <MenuItemLink page={Pages.practice} />
        <MenuItemLink page={Pages.profile} />
        <MenuItemLink page={Pages.help} />
        <MenuItemLink page={Pages.blog} />
        <MenuItemLink page={Pages.highScores} />
        <MenuItemLink page={Pages.multiplayer} />
        <MenuItemLink page={Pages.typingTest} />
        <MenuItemLink page={Pages.layouts} />
      </div>

      <div className={styles.spacer} />

      <div className={styles.utility}>
        <SettingsButton currentPath={currentPath} />
        <AccountLink user={publicUser} anonymized={user?.anonymized ?? false} />
      </div>
    </div>
  );
}

// A single settings gear that consolidates the theme and language controls
// into one popover (replacing the separate theme + locale switchers).
function SettingsButton({ currentPath }: { readonly currentPath: string }) {
  const { color, switchColor } = useTheme();
  const { formatLocalLanguageName } = useIntlDisplayNames();
  const { locale: currentLocale } = usePageData();
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const isLight = color === "apple-light";
  const anchorRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close the settings popover on a click outside it (or Escape). The panel is
  // portaled out of the anchor, so both elements are checked explicitly.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        anchorRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const selectLocale = (locale: string) => {
    setOpen(false);
    window.location.assign(Pages.intlPath(currentPath, locale));
  };

  return (
    <span ref={anchorRef} className={styles.settingsAnchor}>
      <Popover
        open={open}
        anchor={
          <IconButton
            icon={<Icon shape={mdiCogOutline} />}
            title={formatMessage({
              id: "t_Settings",
              defaultMessage: "Settings",
            })}
            onClick={() => {
              setOpen((value) => !value);
            }}
          />
        }
        offset={10}
      >
        <div ref={panelRef} className={styles.settingsMenu}>
          <div className={styles.settingsSection}>
            <div className={styles.settingsLabel}>
              <FormattedMessage id="t_Theme" defaultMessage="Theme" />
            </div>
            <div className={styles.themeSeg}>
              <button
                type="button"
                className={clsx(
                  styles.themeSegBtn,
                  !isLight && styles.themeSegBtnActive,
                )}
                onClick={() => {
                  switchColor("apple-dark");
                }}
              >
                <FormattedMessage id="t_Theme_Dark" defaultMessage="Dark" />
              </button>
              <button
                type="button"
                className={clsx(
                  styles.themeSegBtn,
                  isLight && styles.themeSegBtnActive,
                )}
                onClick={() => {
                  switchColor("apple-light");
                }}
              >
                <FormattedMessage id="t_Theme_Light" defaultMessage="Light" />
              </button>
            </div>
          </div>
          <div className={styles.settingsSection}>
            <div className={styles.settingsLabel}>
              <FormattedMessage id="t_Language" defaultMessage="Language" />
            </div>
            <ul role="menu" className={styles.localeList}>
              {allLocales.map((locale) => (
                <li
                  key={locale}
                  role="menuitem"
                  className={clsx(
                    styles.localeItem,
                    locale === currentLocale && styles.localeItem_selected,
                  )}
                  onClick={() => {
                    selectLocale(locale);
                  }}
                >
                  <span className={styles.localeFlag}>
                    {localeFlag(locale)}
                  </span>
                  <span className={styles.localeItemLabel}>
                    {formatLocalLanguageName(locale)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Popover>
    </span>
  );
}

function AccountLink({
  user,
  anonymized,
}: {
  readonly user: AnyUser;
  readonly anonymized: boolean;
}) {
  // Signed out → a "Start Free" call-to-action; signed in → the account link.
  if (user.id == null) {
    return (
      <NavLink to={Pages.account.path} className={styles.startFree}>
        <FormattedMessage id="t_Start_Free" defaultMessage="Start Free" />
      </NavLink>
    );
  }
  return (
    <NavLink
      className={({ isActive }) =>
        clsx(styles.accountLink, isActive && styles.isActive)
      }
      to={Pages.account.path}
    >
      <Avatar user={user} size="normal" anonymized={anonymized} />
      <span className={styles.userName}>{user.name}</span>
    </NavLink>
  );
}

function MenuItemLink({
  page: {
    path,
    link: { label, title, external },
  },
}: {
  readonly page: PageInfo;
}) {
  const { formatMessage } = useIntl();
  if (external) {
    // Not part of this app's router (e.g. Blog, reverse-proxied to a
    // separate app at the same path) — a real browser navigation, not a
    // client-side route match.
    return (
      <a
        className={styles.link}
        href={path}
        title={title && formatMessage(title)}
      >
        <span className={styles.label}>{formatMessage(label)}</span>
      </a>
    );
  }
  return (
    <NavLink
      className={({ isActive }) =>
        clsx(styles.link, isActive && styles.isActive)
      }
      to={path}
      title={title && formatMessage(title)}
    >
      <span className={styles.label}>{formatMessage(label)}</span>
    </NavLink>
  );
}
